import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppError } from '@mona/shared/utils';
import { isConfigRequest } from '../helpers';
import { FeEventsService } from './fe-events.service';
import { REQUEST_ID_HEADER } from './request-id.interceptor';

/**
 * Tracks HTTP errors already emitted as `http_failure` FE events, so
 * AppErrorHandler skips the redundant generic `error` event.
 */
export const httpErrorsAlreadyEmitted = new WeakSet<Error>();

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private appErrorHandler: ErrorHandler & { handleApiError?: AnyFn };
    private feEvents?: FeEventsService;
    /**
     * Constructor
     *
     * @param injector
     */
    constructor(private injector: Injector) {
        this.appErrorHandler = this.injector.get(ErrorHandler);
    }

    /**
     * Intercept
     *
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startedAt = Date.now();
        return next.handle(request).pipe(
            catchError(err => {
                if (isConfigRequest(request)) {
                    return throwError(err);
                }
                if (err instanceof HttpErrorResponse) {
                    // Emit before handing off to the app error handler
                    // so even if downstream throws (toast service, etc.)
                    // the observability event still lands.
                    this.emitHttpFailure(request, err, startedAt);
                    const appError = new AppError(err);
                    httpErrorsAlreadyEmitted.add(appError as Error);
                    this.appErrorHandler.handleApiError(appError);
                    return throwError(appError);
                }
                return throwError(err);
            }),
        );
    }

    /**
     * Best-effort, swallows everything. The interceptor is on the
     * critical request path; an exception here would mask the
     * underlying HTTP error from the user.
     * @param request
     * @param err
     * @param startedAt
     */
    private emitHttpFailure(request: HttpRequest<unknown>, err: HttpErrorResponse, startedAt: number): void {
        try {
            if (!this.feEvents) {
                this.feEvents = this.injector.get(FeEventsService, null);
            }
            if (!this.feEvents) {
                return;
            }
            const requestId = request.headers.get(REQUEST_ID_HEADER) ?? undefined;
            this.feEvents.emit(
                'http_failure',
                {
                    method: request.method,
                    url: request.url,
                    status: err.status,
                    statusText: err.statusText,
                    durationMs: Date.now() - startedAt,
                },
                requestId,
            );
        } catch {
            // intentional swallow — see method docstring
        }
    }
}
